import React from 'react';
import PropTypes from 'prop-types';
import EditorField from '../Editor/editor-field';
import { checkValueType } from '../Editor/util';
import { InputV2 } from '../v2';
import { InputShape } from '../v2/input';

// It should render a dropdown or input, based on the action value type
// It should render a list of options
// Clicking the option should set the action value types
// The dropdown should support attributes quoted in double curly braces

const getAgentId = (object) => object.payload.userId || object.payload.agentId;

const checkPayloadValueType = (type, payload) => {
  switch (type) {
    case 'TRANSFER_TO_AGENT':
      return checkValueType(getAgentId({ payload })) === 'attribute' ? 'attribute' : 'agent';
    case 'TRANSFER_TO_SKILL':
      return checkValueType(payload.skillId) === 'attribute' ? 'attribute' : 'skill';
    default:
      return checkValueType(payload.message) === 'attribute' ? 'attribute' : 'string';
  }
};

function PolicyActionValue(props) {
  const {
    rowItem,
    rowIndex,
    isEditing,
    temporaryPolicy,
    customAttributes,
    agents,
    skills,
    validateObject,
  } = props;

  const valueType = checkPayloadValueType(rowItem.type, rowItem.payload);

  const isAttribute = valueType === 'attribute';
  const isString = valueType === 'string';
  const isErrored = ['message', 'skillId', 'userId'].some((item) => rowItem?.errorArray?.includes(item));

  const getValueTypes = (type) => {
    switch (type) {
      case 'TRANSFER_TO_AGENT':
        return ['agent', 'attribute'];
      case 'TRANSFER_TO_SKILL':
        return ['skill', 'attribute'];
      default:
        return ['string', 'attribute'];
    }
  };

  const selectPayloadItem = (type, item, index) => {
    const newObject = { ...temporaryPolicy };
    switch (type) {
      case 'TRANSFER_TO_AGENT':
        delete (newObject.actions[index].payload.agentId);
        newObject.actions[index].payload.userId = isAttribute ? item : item.id;
        break;
      case 'TRANSFER_TO_SKILL':
        newObject.actions[index].payload.skillId = isAttribute ? item : item.id;
        break;
      default:
        newObject.actions[index].payload.message = item;
        break;
    }
    validateObject(newObject);
  };

  const getPayloadDropdownValue = () => {
    if (rowItem.type === 'TRANSFER_TO_SKILL') {
      if (isAttribute && typeof rowItem.payload.skillId === 'string') {
        return rowItem.payload.skillId.trim().substr(2).slice(0, -2) || '';
      }
      if (skills.length) {
        const foundItem = skills.find((skill) => `${skill.id}` === `${rowItem.payload.skillId}`);
        if (foundItem) {
          return foundItem;
        }
      }
    }
    if (rowItem.type === 'TRANSFER_TO_AGENT') {
      const agentId = getAgentId(rowItem);
      if (isAttribute && typeof agentId === 'string') {
        return agentId.trim().substr(2).slice(0, -2) || '';
      }
      if (agents.length) {
        const foundItem = agents.find((agent) => `${agent.id}` === `${agentId}`);
        if (foundItem) {
          return foundItem;
        }
      }
    }
    if (rowItem.type === 'SEND_MESSAGE') {
      if (isAttribute && typeof rowItem.payload.message === 'string') {
        return rowItem.payload.message.substr(2).slice(0, -2);
      }
      return rowItem.payload.message || '';
    }
    return '';
  };

  const getPayloadValue = () => {
    if (rowItem.type === 'SEND_MESSAGE') {
      return rowItem.payload.message;
    }
    if (rowItem.type === 'TRANSFER_TO_SKILL' && skills.length) {
      if (isAttribute) {
        return rowItem.payload.skillId;
      }
      const foundItem = skills.find((skill) => `${skill.id}` === `${rowItem.payload.skillId}`);
      if (foundItem) {
        return foundItem.name;
      }
    }
    if (rowItem.type === 'TRANSFER_TO_AGENT' && agents.length) {
      const agentId = getAgentId(rowItem);
      if (isAttribute) {
        return agentId;
      }
      const foundItem = agents.find((agent) => `${agent.id}` === `${agentId}`);
      if (foundItem) {
        return foundItem.fullName;
      }
    }
    return '';
  };

  const selectType = (type) => {
    const newObject = { ...temporaryPolicy };
    if (rowItem.type === 'SEND_MESSAGE') {
      switch (type) {
        case 'attribute':
          newObject.actions[rowIndex].payload = { message: '{{}}' };
          break;
        default:
          newObject.actions[rowIndex].payload = { message: '' };
      }
    }
    if (rowItem.type === 'TRANSFER_TO_SKILL') {
      switch (type) {
        case 'attribute':
          newObject.actions[rowIndex].payload = { skillId: '{{}}' };
          break;
        default:
          newObject.actions[rowIndex].payload = { skillId: '' };
      }
    }
    if (rowItem.type === 'TRANSFER_TO_AGENT') {
      switch (type) {
        case 'attribute':
          newObject.actions[rowIndex].payload = { agentId: '{{}}' };
          break;
        default:
          newObject.actions[rowIndex].payload = { agentId: '' };
      }
    }
    validateObject(newObject);
  };

  const getPlaceholder = () => {
    let placeholder = '';
    if (rowItem.type === 'SEND_MESSAGE') {
      switch (valueType) {
        case 'attribute':
          placeholder = 'Choose an attribute';
          break;
        default:
          placeholder = 'Enter a message';
      }
    }
    if (rowItem.type === 'TRANSFER_TO_SKILL') {
      switch (valueType) {
        case 'attribute':
          placeholder = 'Choose an attribute';
          break;
        default:
          placeholder = 'Choose a skill';
      }
    }
    if (rowItem.type === 'TRANSFER_TO_AGENT') {
      switch (valueType) {
        case 'attribute':
          placeholder = 'Choose an attribute';
          break;
        default:
          placeholder = 'Choose an agent';
      }
    }
    if (rowItem.type === 'fallback-skill') {
      placeholder = 'Choose an attribute (optional)';
    }
    if (rowItem.type === 'fallback-attribute') {
      placeholder = 'Choose an attribute (optional)';
    }
    return placeholder;
  };

  const getItems = (type) => {
    switch (type) {
      case 'agent':
        return agents;
      case 'skill':
        return skills;
      default:
        return customAttributes;
    }
  };

  const payloadValue = getPayloadDropdownValue(valueType, rowItem.value);

  const component = () => {
    if (isEditing) {
      if (isString) {
        return (
          <InputV2
            isErrored={isErrored}
            value={getPayloadValue()}
            placeholder={getPlaceholder()}
            onChange={(event) => selectPayloadItem(rowItem.type, event.target.value, rowIndex)}
            shape={InputShape.SQUARE}
            testId="editor-text"
          />
        );
      }
      return (
        <EditorField
          isEditing={isEditing}
          actionType={rowItem.type}
          value={payloadValue}
          selectItem={(name) => {
            if (typeof name !== 'object' && isAttribute) {
              selectPayloadItem(rowItem.type, `{{${name}}}`, rowIndex);
            } else {
              selectPayloadItem(rowItem.type, name, rowIndex);
            }
          }}
          items={getItems(valueType)}
          noFreeText={!isAttribute}
          placeholder={getPlaceholder()}
          isErrored={isErrored}
        />
      );
    }
    return (
      <EditorField
        isEditing={isEditing}
        actionType={rowItem.type}
        value={payloadValue}
        selectItem={(name) => {
          if (typeof name !== 'object' && isAttribute) {
            selectPayloadItem(rowItem.type, `{{${name}}}`, rowIndex);
          } else {
            selectPayloadItem(rowItem.type, name, rowIndex);
          }
        }}
        items={getItems(valueType)}
        noFreeText={!isAttribute}
        placeholder={getPlaceholder()}
        isErrored={isErrored}
      />
    );
  };

  return (
    <>
      {isEditing && (getValueTypes(rowItem.type).length) && (
        <EditorField
          isEditing
          value={valueType}
          selectItem={(type) => selectType(type)}
          items={getValueTypes(rowItem.type)}
          noFreeText
        />
      )}
      {component()}
    </>
  );
}

PolicyActionValue.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  temporaryPolicy: PropTypes.object.isRequired,
  customAttributes: PropTypes.array.isRequired,
  agents: PropTypes.array.isRequired,
  skills: PropTypes.array.isRequired,
  rowItem: PropTypes.object.isRequired,
  rowIndex: PropTypes.number.isRequired,
  validateObject: PropTypes.func.isRequired,
};

export default PolicyActionValue;
